import { useTheme } from '@emotion/react';
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import * as yup from 'yup';
import ModalHeader from '../../../../components/ModalHeader';
import { Button, Dialog, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import FormTextField from '../../../../components/FormTextField';
import AddIcon from '@mui/icons-material/Add';
import { buttonStyle2, dialogButtonCancel } from '../../../../utils/commonStyle';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { setLoading } from '../../../../redux/features/appSlice';
import { showmessage } from '../../../../utils/toastr';
import appapi from '../../../../api/apiClient';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const validationSchema = yup.object({
  price: yup.number('price').required(' '),
  showValidity: yup.boolean(),
  validity: yup
    .date()
    .nullable()
    .when('showValidity', (showValidity, schema) => {
      if (showValidity) {
        return schema.required();
      }
      return schema;
    }),
  remarks: yup.string('Remarks').required(' ').min(3, 'Remarks should be atleast 3 character').max(300, 'Maximum Character Limit 300').required(' '),
});

const sx = {
  '& .MuiDialog-container': {
    alignItems: 'center',
    WebkitAlignItems: 'center',
    WebkitBoxAlign: 'center',
  },
  '& .MuiPaper-root': {
    width: '560px',
    height: '100%',
    borderRadius: '4px',
    maxWidth: '736px',
    maxHeight: 'calc(100%)',
    // overflow: 'hidden'
  },
};

export default function SendQuote({ modelOpen, onModelHandleClose, data }) {
  const formRef = useRef();
  const theme = useTheme();
  const dispatch = useDispatch();

  const text = theme.palette.primary.text;
  const textLight = theme.palette.primary.textLight;

  const { user, companyDetails } = useSelector((state) => state.auth);

  const {t} = useTranslation()

  const handleSendQuote = async (values) => {
    dispatch(setLoading(true));
    const response = await appapi.put(`/trader/sendQuote/${data?._id}`, values);
    if (response.ok) {
      dispatch(setLoading(false));
      const data = response.data;
      if (data.status === '7400') {
        onModelHandleClose(true);
        showmessage(data.message);
      } else {
        showmessage(data.message, 'error');
      }
    } else {
      dispatch(setLoading(false));
      showmessage(`Something Went Wrong!`, 'error', 2000);
    }
  };

  return (
    <Dialog
      sx={sx}
      open={modelOpen}
      PaperProps={{ sx: { position: 'fixed', top: 0, right: 0, m: 0 } }}
    >
      <Stack
        // className="!h-[100%]"
        style={{ backgroundColor: '#FFFFFF' }}
        flex={1}
      >
        <ModalHeader
          label={t('SEND QUOTE')}
          onClose={() => onModelHandleClose(false)}
        />
        <Stack
          style={{ backgroundColor: '#8993AC26', width: '100%', height: '2px' }}
          className=""
        />
        <Formik
          innerRef={formRef}
          enableReinitialize={false}
          initialValues={{
            price: data?.price ? data?.price : undefined,
            validity: null,
            showValidity: false,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleSendQuote(values);
          }}
        >
          {({ name, values, touched, errors, handleChange, handleBlur, isValid, dirty, setFieldValue, setFieldTouched }) => (
            <Form
              className="flex flex-col flex-1"
              autoComplete="off"
            >
              <Stack
                style={{ backgroundColor: '#F2F3F580', borderRadius: '8px', borderWidth: '1px', borderColor: '#8993AC26' }}
                className="m-[15px] p-[16px]"
              >
                <Typography
                  className="fw-regular ff-Roboto fs-16 !capitalize"
                  color={text}
                >
                  {t('Request Details')}
                </Typography>
                <Typography
                  className="fw-regular ff-Roboto fs-12 !mb-[16px]"
                  color={textLight}
                >
                  {t('Some info may be visible to other people using ZeroEmission services')}
                </Typography>
                <Stack
                  direction={'row'}
                  className="!mb-[16px]"
                >
                  <Stack flex={0.5}>
                    <Typography
                      className="fs-12 ff-Roboto fw-regular"
                      color={text}
                    >
                      {t('Client')}
                    </Typography>
                    <Typography
                      className="fs-14 ff-Roboto fw-medium capitalize"
                      color={text}
                      style={{ maxWidth: '225px' }}
                    >
                      {data?.companyName}
                    </Typography>
                  </Stack>
                  <Stack flex={0.5}>
                    <Typography
                      className="fs-12 ff-Roboto fw-regular"
                      color={text}
                    >
                      {t('Timestamp')}
                    </Typography>
                    <Typography
                      className="fs-14 ff-Roboto fw-medium"
                      color={text}
                    >
                      {moment(data?.createdOn).format('DD-MMM-YYYY HH:mm')}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  direction={'row'}
                  className="!mb-[16px]"
                >
                  <Stack flex={0.5}>
                    <Typography
                      className="fs-12 ff-Roboto fw-regular"
                      color={text}
                    >
                      {t('Type')}
                    </Typography>
                    <Typography
                      className="fs-14 ff-Roboto fw-medium capitalize"
                      color={text}
                      style={{ maxWidth: '225px' }}
                    >
                      {t(data?.priceType)}/{t(data?.tradeType)}
                    </Typography>
                  </Stack>
                  <Stack flex={0.5}>
                    <Typography
                      className="fs-12 ff-Roboto fw-regular"
                      color={text}
                    >
                      EUA
                    </Typography>
                    <Typography
                      className="fs-14 ff-Roboto fw-medium"
                      color={text}
                    >
                      {data?.euaRequired}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack
                  direction={'row'}
                  className="!mb-[16px]"
                >
                  <Stack flex={0.5}>
                    <Typography
                      className="fs-12 ff-Roboto fw-regular"
                      color={text}
                    >
                      {t('Remark')}
                    </Typography>
                    <Typography
                      className="fs-14 ff-Roboto fw-medium"
                      color={text}
                      style={{ maxWidth: '225px' }}
                    >
                      {data?.remarks || '-'}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Stack
                style={{ backgroundColor: '#F2F3F580', borderRadius: '8px', borderWidth: '1px', borderColor: '#8993AC26' }}
                className="m-[15px] p-[16px] !mb-[200px]"
              >
                <Typography
                  className="fw-regular ff-Roboto fs-16 !capitalize"
                  color={text}
                >
                  {t('Quote Details')}
                </Typography>
                <Typography
                  className="fw-regular ff-Roboto fs-12 !mb-[16px]"
                  color={textLight}
                >
                  {t('Some info may be visible to other people using ZeroEmission services')}
                </Typography>
                <Stack>
                  <FormTextField
                    outlineLabel={t('Price / EUA')}
                    name="price"
                    requiredMark={true}
                    onlyFloat={true}
                    whiteSpace={true}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                    sx={{ marginBottom: '10px' }}
                    inputProps={{ maxLength: 10 }}
                  />
                </Stack>
                <Stack
                  alignItems={'flex-start'}
                  className="mb-[10px]"
                >
                  <Button
                    sx={{ color: theme.palette.primary.main }}
                    className="fs-12 ff-Roboto fw-medium uppercase"
                    onClick={() => {
                      setFieldValue('showValidity', true);
                    }}
                    variant="text"
                    startIcon={<AddIcon />}
                  >
                    {t('Add Validity')}
                  </Button>
                </Stack>
                {values?.showValidity && (
                  <Stack className="mb-[20px] mt-3">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        minDate={dayjs().set('hour', 0).set('minute', 0).set('second', 0)}
                        timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                        label={t('Quote valid till') + ' *'}
                        value={values.validity}
                        onChange={(newValue) => setFieldValue('validity', newValue)}
                        renderInput={(params) => (
                          <TextField
                            onKeyDown={(event) => {
                              event.preventDefault();
                            }}
                            variant="outlined"
                            {...params}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Stack>
                )}
                <Stack>
                  <FormTextField
                    outlineLabel={t('Remark')}
                    name="remarks"
                    multiline={true}
                    maxRows={3}
                    requiredMark={true}
                    inputProps={{ maxLength: 301 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: '88px !important',
                        display: 'block',
                      },
                    }}
                  />
                </Stack>
              </Stack>
              <Stack flexGrow={1} />
              <Stack
                direction="row"
                justifyContent="flex-end"
                className="px-4 py-2 !justify-end p-[0px] sticky bottom-[0] !mt-[32px]"
                // className="!mt-[44px] h-[66px] "
                style={{ backgroundColor: '#FFFFFF', zIndex: 1 }}
              >
                <Button
                  sx={[
                    dialogButtonCancel,
                    {
                      color: theme.palette.primary.main,
                      '&:hover': {
                        // backgroundColor: theme.palette.secondary.main,
                        // color: theme.palette.background.main,
                        // borderColor: theme.palette.secondary.main,
                      },
                    },
                  ]}
                  style={{ width: '128px', marginLeft: '80px', height: '40px' }}
                  onClick={() => {
                    onModelHandleClose(false);
                  }}
                >
                  {t('Close')}
                </Button>
                <Button
                  sx={[
                    buttonStyle2,
                    {
                      color: '#fff',
                      backgroundColor: theme.palette.primary.main,
                      textTransform: 'uppercase',
                      '&:hover': {
                        backgroundColor: theme.palette.background.main,
                        color: '#fff',
                      },
                      '&:disabled': {
                        color: 'rgba(255,255,255,0.5)',
                        backgroundColor: theme.palette.primary.main,
                      },
                    },
                  ]}
                  style={{ width: '128px', height: '40px' }}
                  disabled={!(dirty && isValid)}
                  type="submit"
                  variant="contained"
                >
                  {t('Send')}
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>
    </Dialog>
  );
}
